import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, MapPin, ChevronDown } from "lucide-react";

const LOCATIONS = {
  baltimore: { label: "Baltimore, MD", booking: "mailto:dreadsports.baltimore@gmail.com?subject=Court%20Booking%20Request%20%E2%80%94%20Baltimore", login: "https://crf.clubautomation.com" },
  princeton: { label: "Princeton, NJ", booking: "https://njsc.clubautomation.com", login: "https://njsc.clubautomation.com" },
};

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [selectedLoc, setSelectedLoc] = useState(() => localStorage.getItem("lrg-location") || "baltimore");
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setMobileMenuOpen(false); setLocOpen(false); }, [location]);

  const switchLoc = (loc) => { setSelectedLoc(loc); localStorage.setItem("lrg-location", loc); setLocOpen(false); };
  const loc = LOCATIONS[selectedLoc];

  const navItems = [
    { label: "Baltimore", href: "/baltimore" },
    { label: "Princeton", href: "/princeton" },
    { label: "College Consulting", href: "/college-consulting" },
    { label: "Contact", href: "/contact" },
  ];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "glass-dark border-b border-[#232527]" : ""}`} data-testid="header">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
              <div className="w-1.5 h-1.5 bg-[#0D6B56] rounded-full"></div>
              <span className="font-display text-sm font-semibold tracking-tight text-[#F2EDE4]">
                LRG Squash
              </span>
            </Link>

            <nav className="hidden lg:flex items-center gap-0.5">
              {navItems.map((item) => (
                <Link key={item.label} to={item.href}
                  className={`px-3 py-2 text-[11px] font-medium uppercase tracking-[0.08em] transition-colors duration-300 ${location.pathname === item.href ? "text-[#0D6B56]" : "text-[#4A4D50] hover:text-[#F8F7F3]"}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}>
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              {/* Location selector */}
              <div className="relative hidden md:block">
                <button onClick={() => setLocOpen(!locOpen)} className="loc-badge" data-testid="location-selector">
                  <MapPin className="w-3 h-3" />
                  {loc.label}
                  <ChevronDown className="w-3 h-3" />
                </button>
                {locOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-[#161819] border border-[#232527] rounded-sm py-1 min-w-[180px] z-50">
                    {Object.entries(LOCATIONS).map(([key, val]) => (
                      <button key={key} onClick={() => switchLoc(key)}
                        className={`block w-full text-left px-4 py-2.5 text-xs transition-colors ${selectedLoc === key ? "text-[#0D6B56] bg-[#1C1E20]" : "text-[#8A8D90] hover:text-[#F8F7F3] hover:bg-[#1C1E20]"}`}>
                        {val.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <a href={loc.login} target="_blank" rel="noopener noreferrer"
                className="hidden md:inline-flex text-[10px] font-medium uppercase tracking-[0.08em] text-[#4A4D50] hover:text-[#F8F7F3] transition-colors px-3 py-2" data-testid="member-login">
                Member Login
              </a>

              <Link to="/contact" className="hidden md:inline-flex btn-primary text-[10px] px-4 py-2" data-testid="cta-start">
                Start Your Journey
              </Link>

              <button className="lg:hidden p-2 text-[#8A8D90]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-button" aria-label="Toggle menu">
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden mobile-menu-bg min-h-screen" data-testid="mobile-menu">
            <nav className="container-custom py-6 space-y-0">
              {/* Location toggle */}
              <div className="flex gap-2 mb-6 pb-6 border-b border-[#232527]">
                {Object.entries(LOCATIONS).map(([key, val]) => (
                  <button key={key} onClick={() => switchLoc(key)}
                    className={`loc-badge ${selectedLoc === key ? 'active' : ''}`}>
                    <MapPin className="w-3 h-3" /> {val.label}
                  </button>
                ))}
              </div>
              {navItems.map((item) => (
                <Link key={item.label} to={item.href}
                  className="block py-3.5 text-xs font-medium uppercase tracking-[0.08em] text-[#4A4D50] hover:text-[#F8F7F3] border-b border-[#232527] transition-colors duration-300">
                  {item.label}
                </Link>
              ))}
              <a href={loc.login} target="_blank" rel="noopener noreferrer"
                className="block py-3.5 text-xs font-medium uppercase tracking-[0.08em] text-[#4A4D50] hover:text-[#F8F7F3] border-b border-[#232527]">
                Member Login
              </a>
              <Link to="/contact" className="block mt-6 btn-primary text-center text-xs">
                Start Your Journey
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Mobile sticky bottom bar */}
      <div className="md:hidden mobile-action-bar" data-testid="mobile-action-bar">
        <Link to="/contact" className="bar-primary">Start</Link>
        <a href={loc.booking} target="_blank" rel="noopener noreferrer">Book Court</a>
        <a href={loc.login} target="_blank" rel="noopener noreferrer">Login</a>
      </div>
    </>
  );
};

export default Header;
